import { useState } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { Combobox, NoBorderButtonWrap, SimpleChipItemWrap, Tooltip } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";
import { translate } from "app/filters/i18nFilter";
import { creativeTypeToInfoMap } from "app/pages/digital-marketing/ad-intelligence/common/ads/hooks/useAdsGallery";
import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const ComboboxItemContent = styled(CenteredFlexRow)`
    ${SWReactIcons} {
        margin-right: 8px;
    }
`;

interface IAdIntelligenceAdsExtendedFilters {
    onFormatFilterChange: (format: string) => void;
    selectedFormat: string;
    isFromNewScraper: boolean;
}

interface IDropdownItem {
    id: string;
    text: string;
    icon?: IconName;
}

const ALL_FORMATS = "all";

export const AdIntelligenceAdsExtendedFilters = ({
    onFormatFilterChange,
    selectedFormat,
    isFromNewScraper,
}: IAdIntelligenceAdsExtendedFilters) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedFormatText = selectedFormat ? translate(creativeTypeToInfoMap[selectedFormat].name) : "";

    const handleFormatFilterChange = (format: { id?: string }) => {
        const selectedFormatId = format?.id === ALL_FORMATS ? undefined : format?.id;

        if (format) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter Format/${format.id}`);
        }
        onFormatFilterChange(selectedFormatId);
        setIsOpen(false);
    };

    const handleCloseItem = () => {
        onFormatFilterChange(undefined);
    };

    const formatItems: IDropdownItem[] = [
        {
            id: ALL_FORMATS,
            text: translate("ad.intelligence.ads.widget.filters.format.all"),
        },
        ...Object.entries(creativeTypeToInfoMap)
            .filter(([_, info]) => info?.isNewDataFormat === undefined || info.isNewDataFormat === isFromNewScraper)
            .map(([type, info]) => ({
                id: type,
                text: translate(info.name),
                icon: info.icon,
            })),
    ];

    return (
        <Tooltip
            content={translate("ad.intelligence.ads.widget.filters.format.tooltip")}
            enabled={!!selectedFormatText}
        >
            <span>
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    width="140px"
                >
                    <Combobox.Trigger>
                        <span>
                            {selectedFormatText ? (
                                <SimpleChipItemWrap
                                    text={selectedFormatText}
                                    isCompact={false}
                                    onCloseItem={handleCloseItem}
                                />
                            ) : (
                                <NoBorderButtonWrap>
                                    {translate("ad.intelligence.ads.widget.filters.format")}
                                </NoBorderButtonWrap>
                            )}
                        </span>
                    </Combobox.Trigger>

                    <Combobox.Content emptyState={translate("dropdown.no.results")}>
                        {formatItems.map((item: IDropdownItem) => (
                            <Combobox.Option
                                key={item.id}
                                value={item}
                                onClick={() => handleFormatFilterChange({ id: item.id })}
                            >
                                <Combobox.OptionLabel>
                                    <ComboboxItemContent>
                                        {item.icon ? (
                                            <SWReactIcons
                                                iconName={item.icon}
                                                size="sm"
                                            />
                                        ) : null}
                                        {item.text}
                                    </ComboboxItemContent>
                                </Combobox.OptionLabel>
                            </Combobox.Option>
                        ))}
                    </Combobox.Content>
                </Combobox>
            </span>
        </Tooltip>
    );
};
